import { useCallback } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import NotesTable from "@/features/global/components/NotesTable";
import { getNoteParentLink } from "@/features/global/components/NotesTable/noteHelpers";
import { useNoteDeepLink } from "@/features/global/components/NotesTable/useNoteDeepLink";
import { PERSON_DETAILS_HEADER_EDUCATION_QUERY } from "@/features/person/queries";
import { DOMAIN, PERMISSION, useCanManagePersonNotes, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { usePersonNotesAccess } from "../_usePersonNotesAccess";
import { PERSON_NOTES_QUERY } from "./queries";
import { CRMNote } from "@/types";

export default function PersonNotesPage() {
  const { pgaId } = useParams();
  const { loaded, canViewPersonNotes } = usePersonNotesAccess();
  const canManageNotes = useCanManagePersonNotes();

  const canViewPersonEducation = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonEducation
  });

  const { data: educationHeaderData } = useQuery(PERSON_DETAILS_HEADER_EDUCATION_QUERY, {
    variables: { pgaId: pgaId || "" },
    skip: !loaded || !canViewPersonNotes || !pgaId || !canViewPersonEducation
  });

  const universityLegacyId = educationHeaderData?.person?.university?.legacyId;

  const { highlightNoteId, openNoteId, getCopyLink, setOpenNoteInUrl, tableRef } = useNoteDeepLink({
    dataReady: true
  });

  const getParentLink = useCallback(
    (note: CRMNote): string | null => getNoteParentLink(note, { pgaId: pgaId || "", universityLegacyId }),
    [pgaId, universityLegacyId]
  );

  const noteEditing = {
    enabled: canManageNotes,
    notableId: pgaId || "",
    notableType: "PERSON" as const,
    refetchQueries: [PERSON_NOTES_QUERY],
    pgaId: pgaId || "",
    objectType: "person"
  };

  if (!loaded || !canViewPersonNotes) {
    return null;
  }

  return (
    <div ref={tableRef}>
      <div className="pt-12" />
      <NotesTable
        variant="top-level"
        notesQuery={PERSON_NOTES_QUERY}
        queryVariables={{ pgaId: pgaId || "" }}
        headingTopSpacing="none"
        getParentLink={getParentLink}
        getCopyLink={getCopyLink}
        noteEditing={noteEditing}
        highlightNoteId={highlightNoteId}
        openNoteId={openNoteId}
        onOpenNoteChange={setOpenNoteInUrl}
        addNoteVariant="filled"
      />
    </div>
  );
}
